/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { businessStructures, strings } from "../../../common/Strings/Strings";
import { AutoCompleteOptions } from "../../../models/AutoCompleteOptions";
import Field from "../../../components/ReactHookFormFields/General/Field";
import isSwedishOrganizationNumberValid from "../../../util/RegistrationNumberValidator";
import { isSwedishPersonalIdValid } from "../../../util/Validations";
import { BusinessStructure } from "../../../models/clinic/BusinessStructure";
import Select from "../../../components/ReactHookFormFields/General/Select/Select";
import { CountryResponse } from "../../../models/management/CountryResponse";
import { PolicyType } from "../../../models/management/PolicyType";
import AlertBox from "../../../components/AlertBox";
import { ClinicRegistrationRequest } from "../../../models/clinic/ClinicRegistrationRequest";
import Button from "../../../components/Button";
import { PendingClinicRegistrationResponse } from "../../../models/clinic/PendingClinicRegistrationResponse";
import ClinicRegistrationApi from "../../../api/ClinicRegistrationApi";
import { getGeneralError } from "../../../util/helperFunctions";

interface Props {
  registration?: PendingClinicRegistrationResponse;
  countries: CountryResponse[];
  onNext(registration: PendingClinicRegistrationResponse): void;
}

const BasicInformationTab: React.FC<Props> = (props: Props) => {
  const { countries, registration, onNext } = props;
  const [isLoading, setLoading] = useState<boolean>(false);
  const [serverError, setServerError] = useState<null | string>(null);

  const defaultFormValues = (pending?: PendingClinicRegistrationResponse): ClinicRegistrationRequest => ({
    name: pending?.name || "",
    countryCode: pending?.countryCode || "",
    registrationNumber: pending?.registrationNumber || "",
    businessStructure: pending?.businessStructure || BusinessStructure.COMPANY,
    businessName: pending?.businessName || "",
    vat: pending?.vat || "",
  });

  const form = useForm<ClinicRegistrationRequest>({
    mode: "onSubmit",
    defaultValues: defaultFormValues(registration),
  });

  const {
    register,
    getValues,
    formState: { errors },
    watch,
    handleSubmit,
    reset,
  } = form;

  const businessStructure = watch("businessStructure");

  useEffect(() => {
    reset(defaultFormValues(registration));
  }, [registration]);

  const isRegistrationNumberValid = (regNumber: string): boolean => {
    const values = getValues();
    if (values.countryCode === "SE") {
      if (values.businessStructure === BusinessStructure.COMPANY) {
        return isSwedishOrganizationNumberValid(regNumber);
      }
      if (values.businessStructure === BusinessStructure.SOLE_PROPRIETORSHIP) {
        return isSwedishPersonalIdValid(regNumber);
      }
    }
    return true;
  };

  const submit = async (request: ClinicRegistrationRequest) => {
    setLoading(true);
    setServerError(null);

    try {
      let response;
      if (registration) {
        response = await ClinicRegistrationApi.addBasicInformation(registration.id, request);
      } else {
        response = await ClinicRegistrationApi.startRegistration(request);
      }
      onNext(response.data);
    } catch (e) {
      setServerError(await getGeneralError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(submit)}>
        <div className="space-y-6">
          <Select
            autoComplete={AutoCompleteOptions.country}
            disabledChooseOptionTitle={strings.chooseCountry}
            error={errors.countryCode}
            isChooseOption
            label={strings.country}
            name="countryCode"
            options={countries
              ?.filter(
                (i) =>
                  i.availablePolicies.includes(PolicyType.GENERAL_TERMS_AND_CONDITIONS) &&
                  i.availablePolicies.includes(PolicyType.DATA_PROCESSING_TERMS_AND_CONDITIONS)
              )
              ?.map((i) => ({
                title: i.displayName,
                value: i.countryCode,
              }))}
            register={register}
            required
            showRequired
          />
          <Select
            name="businessStructure"
            label={strings.businessStructure}
            register={register}
            options={Object.keys(BusinessStructure).map((type) => ({
              title: businessStructures[type],
              value: type,
            }))}
            error={errors.businessStructure}
            isChooseOption
            required
            showRequired
          />
          <div className="has-helper-text">
            <Field
              name="businessName"
              label={`${businessStructure === BusinessStructure.COMPANY ? strings.businessName : strings.name}`}
              register={register}
              autoComplete={AutoCompleteOptions.organization}
              error={errors.businessName}
              required
              showRequired
            />
            <p>{strings.businessNameInfo}</p>
          </div>
          <Field
            name="registrationNumber"
            label={`${
              businessStructure === BusinessStructure.COMPANY
                ? strings.organisationId
                : strings.registrationNumberLabelForSoleProprietorship
            }`}
            register={register}
            fieldOptions={{
              validate: {
                registrationNumberIsValid: (orgNumber) =>
                  isRegistrationNumberValid(orgNumber) || strings.invalidRegistrationNumber,
              },
            }}
            error={errors.registrationNumber}
            required
            showRequired
          />
          <Field name="vat" label={strings.vatNumber} register={register} error={errors.vat} required showRequired />
          <div className="has-helper-text">
            <Field
              autoComplete={AutoCompleteOptions.off}
              error={errors.name}
              fieldOptions={{
                maxLength: {
                  value: 255,
                  message: strings.formatString(strings.maximumCharacters, 255),
                },
                minLength: {
                  value: 3,
                  message: strings.formatString(strings.minimumCharacters, 3),
                },
              }}
              label={strings.clinicName}
              name="name"
              register={register}
              required
              showRequired
            />
            <p>{strings.clinicNameInfo}</p>
          </div>
          <AlertBox
            hidden={
              errors.name?.type !== "required" &&
              errors.businessName?.type !== "required" &&
              errors.countryCode?.type !== "required" &&
              errors.registrationNumber?.type !== "required" &&
              errors.vat?.type !== "required"
            }
            message={strings.pleaseFillRequiredFields}
          />
          <AlertBox message={serverError} />
          <Button disabled={isLoading} type="submit" loading={isLoading}>
            {strings.saveAndContinue}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default BasicInformationTab;
